import {RequestBody} from "../../entity/core/request-body";
import {toast} from "react-toastify";
import TitleTemplateStore from "../store/title-template-store";
import {TitleTemplateDto} from "../../entity/dto/title-template-dto";
import {TitleTemplate} from "../../entity/model/title-template";
import {Document} from "../../entity/resource/document";
import {CloneTitleTemplateDto} from "../../entity/dto/clone-title-template-dto";
import {AdvancedSearchDto} from "../../entity/dto/advanced-search-dto";
import {TitleBankCache} from "../../entity/resource/title-bank-cache";
import {TitleSearchDto} from "../../entity/dto/title-search-dto";
import {TemplateType} from "../../entity/enums/template-type";

export default class TitleTemplateService {
    titleTemplateStore: TitleTemplateStore;

    constructor(titleTemplateStore: TitleTemplateStore) {
        this.titleTemplateStore = titleTemplateStore;
    }

    getTitleTemplateById = async (organizationId: string, titleTemplateId: string, force: boolean): Promise<TitleTemplate | undefined> => {
        const titleTemplate = this.titleTemplateStore.titleTemplate;

        if (titleTemplate && titleTemplate.id === titleTemplateId && !force)
            return titleTemplate;

        return await this.titleTemplateStore.getTitleTemplateById(organizationId, titleTemplateId);
    }

    exportTitleTemplate = async (organizationId: string, templatePayload: TitleTemplateDto): Promise<void> => {
        const requestBody: RequestBody = { body: templatePayload };
        const response = await this.titleTemplateStore.exportTitleTemplate(organizationId, requestBody);

        if (!response)
            toast.success('Title template created successfully !!');
        else
            toast.error(response);
    }

    importTitleTemplate = async (organizationId: string, titleId: string, body: any): Promise<void> => {
        const requestBody: RequestBody = { body: body };
        const response = await this.titleTemplateStore.importTitleTemplate(organizationId, titleId, requestBody);

        if (!response)
            toast.success('Title template imported successfully !!');
        else
            toast.error(response);
    }

    updateTitleTemplate = async (organizationId: string, titleTemplateId: string, titleTemplateDto: TitleTemplateDto): Promise<void> => {
        const requestBody: RequestBody = { body: titleTemplateDto };
        const response = await this.titleTemplateStore.updateTitleTemplate(organizationId, titleTemplateId, requestBody);

        if (!response)
            toast.success('Title template updated successfully !!');
        else
            toast.error(response);
    }

    deleteTitleTemplate = async (organizationId: string, titleTemplateId: string, isLastItem: boolean): Promise<void> => {
        const response = await this.titleTemplateStore.deleteTitleTemplate(organizationId, titleTemplateId);

        if (!response)
            if (isLastItem)
                toast.success('Title template deleted successfully !!');
        else
            toast.error(response);
    }

    findDocument = (titleTemplate: TitleTemplate, searchInput?: string, documentName?: string): Document | undefined => {
        for (const section of titleTemplate.sections)
            for (const document of section.documents) {
                const documentNameMatch = documentName && document.name.toLowerCase().includes(documentName.toLowerCase());
                const searchInputMatch = searchInput && searchInput !== '' && document.name.toLowerCase().includes(searchInput.toLowerCase());

                if (documentNameMatch || searchInputMatch)
                    return document;
            }

        return undefined;
    }

    cloneTitleTemplate = async (organizationId: string, titleTemplateId: string, cloneTitleTemplateDto: CloneTitleTemplateDto): Promise<void> => {
        const requestBody: RequestBody = { body: cloneTitleTemplateDto };
        const response = await this.titleTemplateStore.cloneTitleTemplate(organizationId, titleTemplateId, requestBody);

        if (!response)
            toast.success('Title template cloned successfully !!');
        else
            toast.error(response);
    }

    getAdvancedSearchDto = (): AdvancedSearchDto => {
        return {
            address: '',
            bookAndPage: '',
            county: '',
            state: '',
            subdivision: '',
            templateName: '',
            tmsNumbers: ''
        }
    }

    getTitleTemplate = (): TitleTemplate | undefined =>  {
        return this.titleTemplateStore.titleTemplate;
    }

    setTitleTemplate = (titleTemplate: TitleTemplate): void =>  {
        this.titleTemplateStore.titleTemplate = titleTemplate;
    }

    setTitleBankCache = (searchInput: string, isPublic: boolean, templates: TitleTemplate[]): void => {
        this.titleTemplateStore.titleBankCache = { searchInput: searchInput, isPublic: isPublic, templates: templates, enabled: false };
    }

    getTitleBankCache = (): TitleBankCache | undefined => {
        return this.titleTemplateStore.titleBankCache;
    }

    clearTitleBankCache = (): void => {
        this.titleTemplateStore.titleBankCache = undefined;
    }

    enabledTitleBankCache = (): void => {
        const cache = this.titleTemplateStore.titleBankCache;
        const updatedTemplate = this.getTitleTemplate();

        if (updatedTemplate && cache?.templates) {
            const titleTemplates = cache.templates.map(template => template.id === updatedTemplate.id ? updatedTemplate : template);
            this.titleTemplateStore.titleBankCache = {
                searchInput: cache.searchInput,
                isPublic: cache.isPublic,
                templates: titleTemplates,
                enabled: true
            };
        }
    }

    getTitleSearchDto = (isPublic: boolean, searchInput?: string): TitleSearchDto => {
        return {
            searchInput: searchInput ?? '',
            orderType: isPublic ? TemplateType.PUBLIC : TemplateType.ORGANIZATION
        }
    }
}